import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import LocationsWeServe from '../components/LocationsWeServe';

const METHODS = [
  { title: 'Online Dating', text: 'Broad access, high volume, requires heavy screening.' },
  { title: 'Local Groups', text: 'Outrigger paddling, run clubs, beach cleanup events.' },
  { title: 'Private Matchmaker', text: 'Vetted introductions, privacy, matched values.' },
];

const COMPARISON = [
  { feature: 'Pool Quality', apps: 'High volume, unverified profiles', niche: 'Moderate volume, interest-focused', matchmaking: 'Hand-picked, background-checked' },
  { feature: 'Time Spent', apps: 'High (hours spent swiping)', niche: 'Moderate (manual messaging)', matchmaking: 'Low (curated introductions)' },
  { feature: 'Privacy Protection', apps: 'Low (public profile photos)', niche: 'Medium (member-only access)', matchmaking: 'High (confidential profile sharing)' },
  { feature: 'Local Focus', apps: 'Mixed (includes tourists)', niche: 'Resident focused', matchmaking: 'Verified residents' },
  { feature: 'Direct Guidance', apps: 'None', niche: 'Automated prompts', matchmaking: 'One-on-one matchmaker support' },
];

const MATCHMAKING_STEPS = [
  { title: 'Confidential Consultation', text: 'Discuss lifestyle, goals & preferences.' },
  { title: 'Screening & Vetting', text: 'Background checks & profile verification.' },
  { title: 'Hand-Picked Introductions', text: 'Review curated matches before meeting.' },
  { title: 'Planned First Date', text: 'Meet in person at a comfortable venue.' },
];

const CULTURAL_TIPS = [
  { title: 'Remove shoes', text: "Always take off your shoes before stepping into someone's home." },
  { title: 'Lei etiquette', text: 'If someone offers you a lei, wear it draped over your shoulders rather than hanging down front like a necklace.' },
  { title: 'Respect family', text: 'Family (ohana) plays a central role in daily plans and big decisions.' },
  { title: 'Keep it grounded', text: 'Boastful talk or pushy behavior alienates local daters quickly.' },
];

const ONLINE_SAFETY = [
  { title: 'Verify identities', text: 'Do a quick video chat before you meet someone in person.' },
  { title: 'Protect personal details', text: 'Keep financial information and home addresses private during early chats.' },
  { title: 'Watch for red flags', text: 'Block accounts that ask for money or ask you to leave the app immediately.' },
];

const FIRST_DATE_SAFETY = [
  { title: 'Meet in public', text: 'Stick to coffee shops, popular restaurants, or busy parks for your first few dates.' },
  { title: 'Drive yourself', text: 'Arrange your own ride to and from the venue.' },
  { title: 'Inform a friend', text: 'Share your date location and plans with a friend. Working with a matchmaker Hawaii agency provides an added safety layer since every client is screened beforehand.' },
];

const SUCCESS_STORIES = [
  { names: 'David and Angela', quote: "From the moment we met, everything just clicked. We bonded over our shared love for adventure, great conversation, and the little things that make life special. What started as casual chats quickly turned into something deeper, and now, we can't imagine life without each other. Love has a way of finding you when you least expect it!" },
  { names: 'Daniel and Sofia', quote: "What started as a simple connection grew into something life-changing. We found a love that's strong, supportive, and full of joy. Now, as a family, every moment feels like a new adventure, and we wouldn't have it any other way. Love has a way of bringing the right people together at just the right time." },
  { names: 'Mark and Lisa', quote: "Our connection started with great conversations and a shared sense of humor, and it quickly grew into something deeper. Every day, we find new reasons to appreciate each other, whether it's through laughter, meaningful talks, or simply enjoying life's little moments together. Finding love at this stage of life has been an incredible journey, and we wouldn't change a thing." },
];

const FAQS = [
  { q: 'What is the best dating site in Hawaii?', a: 'It really depends on what you are looking for. Apps like Tinder or Bumble have the highest number of users on Oahu, but you will spend a lot of time filtering out tourists passing through for the week. Platforms like SLAMMMM let you use location filters and interest tracking for free, which helps you pinpoint local residents.' },
  { q: 'How do I meet singles in Hawaii?', a: "The best approach combines online options with local offline hobbies. Joining an outrigger paddling club, signing up for weekend trail runs, volunteering for beach cleanups, or stopping by neighborhood farmers' markets put you in touch with people who share similar values as yours. For a more direct route, using a local matchmaking service connects you with verified singles who want a serious relationship." },
  { q: 'Does online dating in Hawaii actually work?', a: 'It works well if you set tight location settings and screen profiles carefully. The biggest complaint locals have about dating apps in Hawaii is running into vacationers who leave in three days.' },
  { q: 'How does a matchmaker in Hawaii work?', a: 'Working with a matchmaker is a hands-on, confidential process. You start with a detailed consultation to talk about your background, lifestyle, and relationship goals. The matchmaker then interviews, screens, and background checks potential matches before presenting you with curated profiles. Once both people agree to meet, your matchmaker sets up the date and collects feedback afterward to refine future introductions.' },
  { q: 'Where can I meet single women in Hawaii?', a: 'You can meet single women through local outdoor clubs, community volunteer projects, fitness groups, and neighborhood events. Outside of everyday social activities, many single women in Hawaii prefer curated introductions or vetted matchmaking databases over public swiping apps.' },
  { q: 'What should I know about Hawaiian women dating?', a: 'Honesty, patience, and cultural awareness are essential. Family values and local traditions play a big role in daily life here. Skip flashy sales talk or assumptions based on island stereotypes, and focus on genuine conversation, respect, and open communication.' },
  { q: 'Is dating in Hawaii hard for newcomers?', a: 'It can take time because local communities are close-knit and people want to know if you plan to stay long-term before getting emotionally invested. Showing respect for island customs, getting involved in community events, and proving you are genuinely rooted in Hawaii makes building connections much easier.' },
];

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
});

const SERVICE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dating and Matchmaking in Hawaii',
  serviceType: 'Online Dating Service',
  description: 'SLAMMMM helps singles in Hawaii meet through personal matchmaking and vetted introductions, connecting people based on compatibility, communication style, and relationship goals.',
  url: 'https://www.slammmm.com/hawaii',
  provider: { '@type': 'Organization', name: 'SLAMMMM', url: 'https://www.slammmm.com/' },
  areaServed: {
    '@type': 'State',
    name: 'Hawaii',
    containedInPlace: { '@type': 'Country', name: 'United States' },
  },
  audience: { '@type': 'Audience', audienceType: 'Singles' },
  serviceOutput: { '@type': 'Thing', name: 'Matchmaking introductions' },
});

const Hawaii: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="Dating in Hawaii: Meet Singles and Get Matched"
        description="Looking for dating in Hawaii? Meet singles in Hawaii through personal matchmaking and vetted introductions. Call now for a free consultation."
        canonical="https://www.slammmm.com/hawaii"
        schemas={[FAQ_SCHEMA, SERVICE_SCHEMA]}
      />

      {/* Hero */}
      <section className="py-100 breadcrumb_section bg-light border-bottom">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <div className="section_title">
                <h1 className="mb-4">Dating in Hawaii Made Simple</h1>
              </div>
              <p className="font20 mb-4">
                Dating in Hawaii is possible using three main ways - mobile apps, local social groups, or if you work with a private matchmaker Hawaii agency. As island communities are small and knitted way too closely, meeting active singles in Hawaii needs a realistic approach rather than local social circles.
              </p>
              <div className="card text-start mb-4">
                <h3 className="font20 mb-3">Quick Answer:</h3>
                <ul className="font18 mb-0">
                  <li><strong>The Pool:</strong> Oahu has the largest population, while other neighbor islands have smaller dating pools.</li>
                  <li><strong>The Best Methods:</strong> Use dating applications to choose your date and meet up for hangout sessions.</li>
                  <li><strong>Direct Matching:</strong> A local matchmaker will screen your background details and mutual values before you meet someone.</li>
                </ul>
              </div>
              <a href="tel:156519268"><button className="btn btn-primary">Meet Singles in Hawaii: Call Now: 15-651-9268</button></a>
            </div>
          </div>
        </div>
      </section>

      {/* Dating scene */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Dating Scene in Hawaii</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <h3 className="font22 mb-3">Oahu Singles Scene</h3>
              <p className="font20 mb-0">
                As we have discussed above, Oahu has the largest state&rsquo;s population. Between Honolulu, the South Shore, and Windward towns, you will get a mix of permanent residents, military members, and working professionals.
              </p>
            </div>
            <div className="col-md-6">
              <h3 className="font22 mb-3">Neighbor Island Dating</h3>
              <p className="font20 mb-0">
                Dating on Maui, Kauai, or the Big Island works differently when compared to Oahu. Since the population is smaller, social circles overlap constantly. In places like Kona, Kahului, or Lihue, chances are high that you will run into familiar faces. Meeting people outside your immediate circle means joining regional community projects or using a structured service.
              </p>
            </div>
            <div className="col-md-6">
              <h3 className="font22 mb-3">Singles by Age Group</h3>
              <p className="font20 mb-0">
                You will find a young crowd near universities, military bases, and hospitality hubs in Honolulu. Mid-career professionals and active seniors are residing in most of the islands, prioritizing long-term stability, family connections, and outdoor habits over nightlife.
              </p>
            </div>
            <div className="col-md-6">
              <h3 className="font22 mb-3">Dating as a Newcomer</h3>
              <p className="font20 mb-0">
                Moving to Hawaii requires adjusting yourself to the place. Locals wait to see if a newcomer plans to stay long-term before they invest emotional energy into a relationship. If you respect local customs and show them that you are rooted here, it helps build trust.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet singles - 3 ways */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Meet Singles in Hawaii</h2>
            </div>
            <p className="font20 mb-0 mt-3">Three ways to meet singles in Hawaii:</p>
          </div>

          <div className="row g-4 mb-5">
            {METHODS.map((m, i) => (
              <div className="col-md-4" key={m.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font22 mb-0">{m.title}</h3>
                    <p className="font28 mb-0 text-primary opacity-50">{i + 1}</p>
                  </div>
                  <p className="mb-0 font20">{m.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4">
            <div className="col-lg-4">
              <h3 className="font22 mb-3">Online Dating in Hawaii</h3>
              <p className="font20 mb-0">
                You can use online dating Hawaii apps to view profiles across islands while sitting comfortable at your home. The main challenge here is filtering out tourists passing through for a week, short-term visitors, and inactive bot accounts. Set a tight distance limit and keep your search focused on actual residents.
              </p>
            </div>
            <div className="col-lg-4">
              <h3 className="font22 mb-3">Clubs, Groups, and Events</h3>
              <p className="font20 mb-0">
                If you guys share common hobbies, meeting offline is a great way to start. Outrigger canoe clubs, weekend trail runs, beach cleanups, and local volunteer groups give you reliable chances to meet singles in Hawaii in a casual, comfortable setting.
              </p>
            </div>
            <div className="col-lg-4">
              <h3 className="font22 mb-3">Personal Matchmaker Introductions</h3>
              <p className="font20 mb-3">
                If you don&rsquo;t want guessing games or swipe constantly, hire a matchmaker Hawaii service that connects you directly with singles in Hawaii. Private introductions will save you time and ensure you both share similar long-term goals.
              </p>
              <a href="tel:156519268"><button className="btn btn-outline-primary">Talk to a Hawaii Matchmaker</button></a>
            </div>
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Best Dating Sites in Hawaii</h2>
            </div>
            <p className="font20 mb-0 mt-3">
              Dating Apps vs. Matchmakers &mdash; let&rsquo;s see how your search depends on your schedule, privacy needs, and expectations. Here is how standard options compare:
            </p>
          </div>

          <div className="table-responsive-blog">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free Swiping Apps</th>
                  <th>Niche Online Sites</th>
                  <th>Private Matchmaking</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>{row.apps}</td>
                    <td>{row.niche}</td>
                    <td>{row.matchmaking}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row g-4 mt-2">
            <div className="col-md-6">
              <h3 className="font22 mb-3">What to Look For</h3>
              <p className="font20 mb-0">
                When choosing among dating sites in Hawaii, check whether the platform verifies local residency. The best dating site in Hawaii for your situation fits your budget while filtering out fake accounts and vacationers.
              </p>
            </div>
            <div className="col-md-6">
              <h3 className="font22 mb-3">Online Dating Pros and Cons</h3>
              <p className="font20 mb-0">
                While online dating Hawaii platforms give you access to new profiles, managing endless chats without meeting them in person can be exhausting. If you combine online search tools with offline activities, it will yield better long-term results for dating in Hawaii.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Matchmaker services */}
      <section className="py-100 our_approach_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Hawaii Matchmaker Services</h2>
            </div>
            <p className="font20 mb-0 mt-3 text-center">The matchmaking process:</p>
          </div>

          <div className="row g-4 mb-5">
            {MATCHMAKING_STEPS.map((step, i) => (
              <div className="col-md-6 col-lg-3" key={step.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font20 mb-0">{step.title}</h3>
                    <p className="font22 mb-0 text-primary opacity-50">{`0${i + 1}`}</p>
                  </div>
                  <p className="mb-0 font18">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4">
            <div className="col-lg-4">
              <h3 className="font22 mb-3">Why Hire a Matchmaker</h3>
              <p className="font20 mb-0">
                A private Hawaii matchmaker will remove trial and error from your search. Instead of reading through multiple bios, you work with a professional who interviews, screens, and introduces compatible matches directly to you.
              </p>
            </div>
            <div className="col-lg-4">
              <h3 className="font22 mb-3">How Matchmaking Works</h3>
              <ul className="font20 mb-0">
                <li><strong>Initial Consultation:</strong> A matchmaking service will review your background, relationship goals, lifestyle habits, and core values.</li>
                <li><strong>Screening and Vetting:</strong> Potential matches undergo identity verification and personal interviews.</li>
                <li><strong>Curated Introductions:</strong> You review detailed profiles before agreeing to meet in person.</li>
                <li><strong>Feedback and Coaching:</strong> After each date, there will be a feedback session to refine future introductions.</li>
              </ul>
            </div>
            <div className="col-lg-4">
              <h3 className="font22 mb-3">Who It&rsquo;s Best For</h3>
              <p className="font20 mb-0">
                Matchmaking is best for busy professionals, long-time residents, and privacy-minded individuals who value quality over profile volume.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Single women */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Single Women in Hawaii</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Hawaiian Woman Dating Etiquette</h3>
              <p className="font20 mb-0">
                When approaching Hawaiian women dating, direct communication and genuine respect matter most. Local culture values family connections and sincerity. Approach conversations without making assumptions based on island stereotypes.
              </p>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Meeting Women Beyond Apps</h3>
              <p className="font20 mb-3">
                Many single women in Hawaii prefer meeting through mutual friends, community activities, or private introductions over dating apps. Building a trusted presence in the local community is a step toward building lasting connections with singles in Hawaii.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Meet Single Women in Hawaii</button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Local etiquette */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Local Dating Etiquette</h2>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="font22 mb-3">Aloha Spirit in Dating</h3>
            <p className="font20 mb-0">
              Aloha means showing kindness, mutual respect, and patience. In dating, that means showing up on time, being honest with communication, and treating service staff and venues with care.
            </p>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Cultural Awareness Tips</h3>
              <ul className="font20">
                {CULTURAL_TIPS.map((t) => (
                  <li key={t.title}><strong>{t.title}:</strong> {t.text}</li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Understanding Local Diversity</h3>
              <p className="font20 mb-0">
                Hawaii blends Native Hawaiian, Japanese, Filipino, Portuguese, and Caucasian cultures. Respecting local foods, traditions, and community history makes dating in Hawaii much more rewarding and easygoing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Date ideas */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Date Ideas in Hawaii</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <h3 className="font22 mb-3">Oahu Date Spots</h3>
              <ul className="font20 mb-0">
                <li><strong>Scenic Hike:</strong> Walk the Makapuu Lighthouse Trail for excellent coastal views.</li>
                <li><strong>Historic Walk:</strong> Tour the grounds of Lolani Palace in downtown Honolulu.</li>
                <li><strong>Sunset Coffee:</strong> Grab coffee at a quiet spot along the Windward Coast.</li>
              </ul>
            </div>
            <div className="col-md-4">
              <h3 className="font22 mb-3">Neighbor Island Date Ideas</h3>
              <ul className="font20 mb-0">
                <li><strong>Maui:</strong> Walk through the Maui Nui Botanical Gardens or stop by the Upcountry Farmers Market.</li>
                <li><strong>Big Island:</strong> Explore historic Kailua Village or visit a Kona coffee farm.</li>
                <li><strong>Kauai:</strong> Meet for lunch in Hanapepe Town or walk along the Kapaa Coastal Path.</li>
              </ul>
            </div>
            <div className="col-md-4">
              <h3 className="font22 mb-3">Free and Budget Dates</h3>
              <p className="font20 mb-0">
                You guys can pack a cooler for an afternoon at the beach, catch a local slack-key guitar performance, or wander through neighborhood farmers&rsquo; markets together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Dating Safety Tips</h2>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-6">
              <h3 className="font22 mb-3">Online Dating Safety</h3>
              <ul className="font20">
                {ONLINE_SAFETY.map((t) => (
                  <li key={t.title}><strong>{t.title}:</strong> {t.text}</li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="font22 mb-3">First Date Safety</h3>
              <ul className="font20">
                {FIRST_DATE_SAFETY.map((t) => (
                  <li key={t.title}><strong>{t.title}:</strong> {t.text}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Success stories */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">Client Success Stories</h2>
            </div>
          </div>

          <div className="row g-4">
            {SUCCESS_STORIES.map((s) => (
              <div className="col-md-4" key={s.names}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom">{s.names}</h3>
                  <p className="mb-0 font18">&ldquo;{s.quote}&rdquo;</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Get Matched Today</button></Link>
          </div>
        </div>
      </section>

      <LocationsWeServe />

      {/* Final CTA / consultation */}
      <section className="py-100 love_journey_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center z-3 position-relative">
              <div className="section_title mb-3">
                <h2 className="text-white">Start Dating in Hawaii</h2>
              </div>
              <p className="text-white font20 mb-4 pb-2">
                Stop wasting time on profiles that are basically inactive. Working with a dedicated matchmaker in Hawaii gives you proper introductions, privacy protections, and local search expertise across all islands. Take the first step toward meeting quality singles in Hawaii today.
              </p>
              <div className="card text-start d-inline-block" style={{ maxWidth: '420px' }}>
                <h3 className="font20 mb-3">Request a Free Consultation</h3>
                <p className="font18 mb-2">Islands we match: Oahu &bull; Maui &bull; Big Island &bull; Kauai</p>
                <a href="tel:156519268" className="d-block mb-2 font18"><i className="fa-solid fa-phone me-2"></i>15-651-9268</a>
                <a href="tel:6102593335" className="d-block mb-2 font18"><i className="fa-solid fa-phone me-2"></i>610-259-3335</a>
                <a href="mailto:slammmm.com@gmail.com" className="d-block mb-3 font18"><i className="fa-solid fa-envelope me-2"></i>slammmm.com@gmail.com</a>
                <a href="tel:156519268"><button className="btn btn-primary w-100">Submit Request &ndash; Call Now</button></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-100 help_faq_section" id="hawaii-faq">
        <div className="container">
          <div className="section_title text-center mb-5">
            <h2 className="mb-0">Dating in Hawaii FAQs</h2>
          </div>

          <div className="row">
            <div className="col-lg-9 mx-auto">
              <div className="accordion" id="hawaii-faq-accordion">
                {FAQS.map((item, index) => (
                  <div className="accordion-item" key={item.q}>
                    <h3 className="accordion-header">
                      <button
                        className={`accordion-button ${index === 0 ? '' : 'collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#hawaii-faq-${index}`}
                        aria-expanded={index === 0 ? 'true' : 'false'}
                        aria-controls={`hawaii-faq-${index}`}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`hawaii-faq-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#hawaii-faq-accordion"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hawaii;
